import { PrizeTierMap } from '@daymade/common-lib/constants';
import Separator from 'components/Separator';
import { CSSProperties } from 'react';
import { ASSET_PREFIX, colors } from 'utils/constants';

const getPrizeWinMessage = (prizeCode?: string) => {
  if (!prizeCode) {
    return null;
  }

  const startMessage = 'You won ';
  let endMessage = 'a DAYMADE prize';

  if (prizeCode === 'TH_TOKEN') {
    endMessage = 'a DAYMADE token';
  } else if (prizeCode === 'TH_TOKEN_X2') {
    endMessage += '2 DAYMADE tokens';
  } else {
    endMessage = `a ${PrizeTierMap[prizeCode as keyof typeof PrizeTierMap]}`;
  }

  return startMessage + endMessage;
};

const hiddenDrawNumber = (width = 20) => {
  return (
    <img
      src={`${ASSET_PREFIX}/images/reveal/question-mark.svg`}
      style={{ width: `${width}px` }}
    />
  );
};

interface EntryLineProps {
  id?: string;
  lineIndex?: number;
  main?: number[];
  resultMain?: number[];
  bonus?: number[];
  resultBonus?: number[];
  drawDate?: string;
  mainNumbersProofUrl?: string;
  bonusNumberProofUrl?: string;
  usingBonusBall?: number | boolean;
  ballSelectHandler?: any;
  selectedBallIndex?: number;
  compareToLine?: number[];
  userEntry?: boolean;
  revealStage?: any;
  skipFlash?: boolean;
  highlightStage?: number;
  prizeCode?: string;
  showPrizeWinMessage?: boolean;
  className?: string;
  style?: CSSProperties;
}

const EntryLine = ({
  id,
  lineIndex,
  main,
  resultMain,
  bonus,
  resultBonus,
  userEntry = false,
  drawDate,
  mainNumbersProofUrl,
  bonusNumberProofUrl,
  usingBonusBall,
  ballSelectHandler,
  revealStage,
  skipFlash,
  highlightStage,
  prizeCode,
  showPrizeWinMessage,
  className,
  style,
}: EntryLineProps) => {
  const line = bonus && main ? main.concat(bonus) : main ? main : [];
  const resultLine =
    resultBonus && resultMain
      ? resultMain.concat(resultBonus)
      : resultMain
        ? resultMain
        : [];

  let lineClassName = 'fullWidth flexRow line';
  if (ballSelectHandler) lineClassName += ' clickable';
  if (className) lineClassName += ` ${className}`;

  const prizeWinMessage = getPrizeWinMessage(prizeCode);

  return (
    <div className={`entryLine fullWidth flexCentered ${userEntry ? 'userEntry' : ''}`}>
      {!userEntry && line && line.length && (
        <>
          <div className="fullWidth flexSpaceBetween">
            <div>
              <strong>Draw date</strong>
            </div>
            <div>{drawDate && <strong>{drawDate}</strong>}</div>
          </div>

          <Separator
            style={{
              backgroundColor: colors.primaryBrandGray,
              margin: '1em 0 2em',
            }}
          />
        </>
      )}

      <div className={lineClassName} id={id} style={style}>
        {line?.map((number, ballIndex) => {
          let ballClassName = 'ball';
          if (revealStage) {
            ballClassName += ' reveal';
          }
          if (usingBonusBall && ballIndex === line.length - 1) {
            ballClassName += ' bonus';
          }

          const renderWinningLineBall = () => {
            ballClassName += ' selected';

            if (revealStage === ballIndex + 1 && !skipFlash) {
              ballClassName += ' revealing';
            }

            let ballContent;
            if (revealStage === ballIndex && highlightStage !== revealStage) {
              ballContent = hiddenDrawNumber(25);
            } else if (revealStage - 1 < ballIndex) {
              ballContent = hiddenDrawNumber();
            } else {
              ballContent = number;
            }

            return (
              <div
                key={ballIndex}
                className={ballClassName}
                onClick={() =>
                  ballSelectHandler && ballSelectHandler(lineIndex, ballIndex)
                }
              >
                {ballContent}
              </div>
            );
          };

          const renderEntryLineBall = () => {
            const ballHighlighted =
              highlightStage && highlightStage - 1 >= ballIndex;
            let ballMatch;
            if (!usingBonusBall || ballIndex < resultLine.length - 1) {
              ballMatch = resultLine
                ?.slice(
                  0,
                  Math.min(
                    revealStage,
                    usingBonusBall ? resultLine.length - 1 : resultLine.length
                  )
                )
                .includes(number);
            } else {
              ballMatch = number === resultLine?.[ballIndex];
            }

            if (ballHighlighted && ballMatch) {
              ballClassName += ' selected';
            }

            return (
              <div
                key={ballIndex}
                className={ballClassName}
                onClick={() =>
                  ballSelectHandler && ballSelectHandler(lineIndex, ballIndex)
                }
              >
                {number}
              </div>
            );
          };

          if (userEntry) {
            return renderEntryLineBall();
          } else {
            return renderWinningLineBall();
          }
        })}
      </div>

      {!userEntry && line && line.length && (
        <>
          <Separator
            style={{
              backgroundColor: colors.primaryBrandGray,
              margin: '2em 0 1em',
            }}
          />

          {mainNumbersProofUrl && bonusNumberProofUrl ? (
            <div className="fullWidth flexSpaceBetween">
              <div>
                <a href={mainNumbersProofUrl} target="_blank">
                  <em>Main number draw</em>
                </a>
              </div>
              <div>
                <a href={bonusNumberProofUrl} target="_blank">
                  <em>Bonus number draw</em>
                </a>
              </div>
            </div>
          ) : (
            <div className="fullWidth flexCentered">
              <a
                href="https://www.instagram.com/daymadeuk/channel"
                target="_blank"
              >
                <em>Watch live draw</em>
              </a>
            </div>
          )}
        </>
      )}

      {showPrizeWinMessage && (
        <>
          <Separator />
          <p style={{ fontWeight: 700 }}>
            <span style={{ marginRight: '0.6em' }}>🏆</span>
            {prizeWinMessage}
            <span style={{ marginLeft: '0.6em' }}>🏆</span>
          </p>
        </>
      )}

      <style jsx>{`
        a {
          text-decoration: underline;
        }

        .entryLine {
          position: relative;
          border: 2px solid
            ${line ? colors.primaryBrandGray : `${colors.primaryBrandGray}20`};
          border-radius: 3px;
          padding: 1.5em;
        }

        .line {
          font-size: 1.8em;
          max-width: 100%;
          flex-grow: 1;
          align-items: center;
          justify-content: space-around;
          transition: 0.2s;
        }

        .line.clickable {
          cursor: pointer;
        }

        :global(.ball, .bonus) {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          font-weight: bold;
          padding: 10px 0px;
          border-bottom: 5px solid white;
          border-top: 5px solid white;
          transition: 0.2s;
          color: #aaa;
          width: 40px;
          height: 50px;
        }

        .ball.selected {
          transition: 0.2s;
          color: ${colors.primaryBrandGray};
          text-shadow: 0 0 9px ${colors.brand.neon};
        }

        .ball.selected.reveal {
          transition: 0.2s;
          text-shadow: 0 0 4px ${colors.brand.neon};
        }

        .ball.bonus {
          color: white;
          border-radius: 50%;
          transition: 0.2s;
          font-size: 0.9em;
        }
        .userEntry .ball.bonus {
          background-color: #999;
        }

        .userEntry .ball.selected.bonus,
        .ball.selected.bonus.revealing {
          height: 40px;
          background-color: ${colors.primaryBrandGray};
          border: 1px solid ${colors.brand.neon};
          box-shadow: 0 0 4px ${colors.brand.neon};
        }
      `}</style>
    </div>
  );
};

export default EntryLine;
